import { useEffect, useRef, useState } from "react";
import { useLocale } from "../ui.js";
import { errorToCode, safeErrorText } from "@smart-shikka/react";

type SaveTarget = "download" | "file" | "directory";
type PreviewKind = "screenshot" | "recording";

interface Preview {
  blob: Blob;
  url: string;
  name: string;
  kind: PreviewKind;
  source: "browser";
  timestamp: string;
  size: number;
  durationSeconds?: number;
  saved: boolean;
}

const TARGET_KEY = "ss.webCapture.target.v1";
const PREFIX_KEY = "ss.webCapture.prefix.v1";
const FOLDER_NAME_KEY = "ss.webCapture.folderName.v1";
const MAX_RECORDING_BYTES = 500 * 1024 * 1024;
const MAX_RECORDING_MS = 30 * 60_000;

function sanitizePrefix(raw: string): string {
  const limited = raw.trim().slice(0, 48);
  const safe = limited.replace(/[^a-zA-Z0-9_.-]/g, "-").replace(/\.{2,}/g, ".");
  return safe || "smart-shikka";
}

function formatTimestamp(at = new Date()): string {
  const y = at.getUTCFullYear();
  const m = String(at.getUTCMonth() + 1).padStart(2, "0");
  const d = String(at.getUTCDate()).padStart(2, "0");
  const h = String(at.getUTCHours()).padStart(2, "0");
  const min = String(at.getUTCMinutes()).padStart(2, "0");
  const s = String(at.getUTCSeconds()).padStart(2, "0");
  return `${y}${m}${d}-${h}${min}${s}`;
}

function fileName(prefix: string, kind: PreviewKind): string {
  const stamp = formatTimestamp();
  const short = `${Math.random().toString(36).slice(2, 8)}`;
  const ext = kind === "screenshot" ? "png" : "webm";
  return `${sanitizePrefix(prefix)}-browser-${kind}-${stamp}-${short}.${ext}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function WebCaptureControls(): JSX.Element {
  const { tr, locale } = useLocale();
  const [target, setTarget] = useState<SaveTarget>(() => {
    try {
      const saved = localStorage.getItem(TARGET_KEY);
      return saved === "file" || saved === "directory" ? saved : "download";
    } catch {
      return "download";
    }
  });
  const [prefix, setPrefix] = useState(() => {
    try {
      return localStorage.getItem(PREFIX_KEY) ?? "smart-shikka";
    } catch {
      return "smart-shikka";
    }
  });
  const [folderName, setFolderName] = useState(() => {
    try {
      return localStorage.getItem(FOLDER_NAME_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingBytesRef = useRef(0);
  const recordingTimeoutRef = useRef<number | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);
  const directoryHandleRef = useRef<unknown>(null);

  useEffect(() => {
    try { localStorage.setItem(TARGET_KEY, target); } catch { /* ignore */ }
  }, [target]);

  useEffect(() => {
    try { localStorage.setItem(PREFIX_KEY, prefix); } catch { /* ignore */ }
  }, [prefix]);

  useEffect(() => {
    try { localStorage.setItem(FOLDER_NAME_KEY, folderName); } catch { /* ignore */ }
  }, [folderName]);

  useEffect(() => {
    if (!recording) return;
    const startedAt = Date.now();
    recordingStartedAtRef.current = startedAt;
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [recording]);

  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview?.url]);

  function setPreviewSafe(next: Preview | null): void {
    setPreview((prev) => {
      if (prev?.url && prev.url !== next?.url) URL.revokeObjectURL(prev.url);
      return next;
    });
  }

  async function chooseDirectory(): Promise<void> {
    const w = window as unknown as { showDirectoryPicker?: () => Promise<unknown> };
    if (typeof w.showDirectoryPicker !== "function") {
      setMessage(tr("captureNotSupported"));
      return;
    }
    try {
      const handle = await w.showDirectoryPicker();
      directoryHandleRef.current = handle;
      const name = (handle as { name?: string }).name ?? "";
      setFolderName(name);
    } catch {
      setMessage(tr("captureCancelled"));
    }
  }

  async function saveBlob(blob: Blob, name: string, ext: "png" | "webm"): Promise<boolean> {
    if (target === "download") {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return true;
    }

    if (target === "file") {
      const w = window as unknown as {
        showSaveFilePicker?: (opts: {
          suggestedName: string;
          types: { description: string; accept: Record<string, string[]> }[];
        }) => Promise<unknown>;
      };
      if (typeof w.showSaveFilePicker !== "function") {
        setMessage(tr("captureNotSupported"));
        return false;
      }
      try {
        const accept = ext === "png" ? { "image/png": [".png"] } : { "video/webm": [".webm"] };
        const description = ext === "png" ? "PNG image" : "WebM video";
        const handle = await w.showSaveFilePicker({
          suggestedName: name,
          types: [{ description, accept }],
        });
        const writable = await (handle as { createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }> }).createWritable();
        await writable.write(blob);
        await writable.close();
        return true;
      } catch {
        setMessage(tr("captureCancelled"));
        return false;
      }
    }

    if (!directoryHandleRef.current) {
      await chooseDirectory();
    }
    const handle = directoryHandleRef.current;
    if (!handle) {
      setMessage(tr("captureCancelled"));
      return false;
    }
    try {
      const dir = handle as {
        requestPermission?: (opts: { mode: string }) => Promise<string>;
        getFileHandle: (name: string, opts: { create: boolean }) => Promise<unknown>;
      };
      if (typeof dir.requestPermission === "function") {
        const perm = await dir.requestPermission({ mode: "readwrite" });
        if (perm !== "granted") {
          setMessage(tr("captureCancelled"));
          return false;
        }
      }
      const fileHandle = await dir.getFileHandle(name, { create: true });
      const writable = await (fileHandle as { createWritable: () => Promise<{ write: (data: Blob) => Promise<void>; close: () => Promise<void> }> }).createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (err) {
      setMessage(`${tr("captureError")}: ${safeErrorText(errorToCode(err), locale).title}`);
      return false;
    }
  }

  async function saveCurrentPreview(as = false): Promise<void> {
    if (!preview) return;
    const ext = preview.kind === "screenshot" ? "png" : "webm";
    const previousTarget = target;
    if (as) setTarget("file");
    const ok = await saveBlob(preview.blob, preview.name, ext);
    if (as) setTarget(previousTarget);
    if (ok) {
      setPreviewSafe({ ...preview, saved: true });
      setMessage(`${tr("captureSaved")}: ${preview.name}`);
    }
  }

  function discardPreview(): void {
    setPreviewSafe(null);
    setMessage(tr("captureCancelled"));
  }

  async function takeScreenshot(): Promise<void> {
    setMessage(null);
    setPreviewSafe(null);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      const width = settings.width ?? 1920;
      const height = settings.height ?? 1080;
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error("video load failed"));
        setTimeout(() => reject(new Error("video load timeout")), 3000);
      });
      await video.play();
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("canvas context unavailable");
      ctx.drawImage(video, 0, 0, width, height);
      stream.getTracks().forEach((t) => t.stop());
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => { if (b) resolve(b); else reject(new Error("toBlob failed")); }, "image/png");
      });
      const name = fileName(prefix, "screenshot");
      const url = URL.createObjectURL(blob);
      setPreviewSafe({
        blob,
        url,
        name,
        kind: "screenshot",
        source: "browser",
        timestamp: new Date().toISOString(),
        size: blob.size,
        saved: false,
      });
    } catch (err) {
      setMessage(`${tr("captureError")}: ${safeErrorText(errorToCode(err), locale).title}`);
    }
  }

  async function startRecording(): Promise<void> {
    setMessage(null);
    setPreviewSafe(null);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      streamRef.current = stream;
      let mimeType = "video/webm";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "";
      }
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recordingBytesRef.current = 0;
      recordingStartedAtRef.current = Date.now();
      recorder.ondataavailable = (event) => {
        if (event.data.size <= 0) return;
        recordingBytesRef.current += event.data.size;
        if (recordingBytesRef.current > MAX_RECORDING_BYTES) {
          setMessage(`${tr("captureError")}: 500 MB safety limit`);
          recorder.stop();
          return;
        }
        chunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        const startedAt = recordingStartedAtRef.current ?? Date.now();
        const durationSeconds = Math.floor((Date.now() - startedAt) / 1000);
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        recorderRef.current = null;
        chunksRef.current = [];
        recordingBytesRef.current = 0;
        if (recordingTimeoutRef.current !== null) window.clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = null;
        setRecording(false);
        setElapsed(0);
        if (blob.size > 0) {
          const name = fileName(prefix, "recording");
          const url = URL.createObjectURL(blob);
          setPreviewSafe({
            blob,
            url,
            name,
            kind: "recording",
            source: "browser",
            timestamp: new Date().toISOString(),
            size: blob.size,
            durationSeconds,
            saved: false,
          });
        } else {
          setMessage(tr("captureCancelled"));
        }
      };
      stream.getVideoTracks()[0]?.addEventListener("ended", () => recorder.stop(), { once: true });
      recorder.start(1000);
      recordingTimeoutRef.current = window.setTimeout(() => recorder.stop(), MAX_RECORDING_MS);
      setRecording(true);
    } catch (err) {
      setMessage(`${tr("captureError")}: ${safeErrorText(errorToCode(err), locale).title}`);
    }
  }

  function stopRecording(): void {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === "recording") recorder.stop();
  }

  const targetOptions: { value: SaveTarget; label: string }[] = [
    { value: "download", label: tr("captureTargetDownload") },
    { value: "file", label: tr("captureTargetFile") },
    { value: "directory", label: tr("captureTargetDirectory") },
  ];

  return (
    <div className="ss-web-capture">
      <p className="section-desc">{tr("captureDesc")}</p>

      <div className="jarvis-select" style={{ marginTop: 12 }}>
        <span>{tr("captureSaveTarget")}</span>
        <select value={target} onChange={(e) => setTarget(e.target.value as SaveTarget)}>
          {targetOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {target === "directory" && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="mono" style={{ flex: 1, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {folderName || tr("captureNoFolder")}
            </span>
            <button className="ss-btn-ghost" type="button" disabled={recording} onClick={() => void chooseDirectory()}>
              {tr("captureChooseFolder")}
            </button>
          </div>
          <p className="muted" style={{ fontSize: 11, margin: 0 }}>{tr("captureChooseFolderNote")}</p>
        </div>
      )}

      <div className="jarvis-select" style={{ marginTop: 12 }}>
        <span>{tr("captureFilenamePrefix")}</span>
        <input
          type="text"
          value={prefix}
          disabled={recording}
          onChange={(e) => setPrefix(e.target.value)}
          placeholder="smart-shikka"
          style={{ padding: "6px 10px", borderRadius: 8, background: "var(--surface-variant)", color: "var(--on-surface)", border: "1px solid var(--border)", fontFamily: "inherit", fontSize: 13 }}
        />
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
        <button className="ss-login-submit" type="button" disabled={recording} onClick={() => void takeScreenshot()}>
          {tr("captureScreenshot")}
        </button>
        {recording ? (
          <button className="jarvis-estop" type="button" onClick={stopRecording}>
            {tr("captureStopRecording")} · {elapsed}s
          </button>
        ) : (
          <button className="ss-login-submit" type="button" disabled={false} onClick={() => void startRecording()}>
            {tr("captureStartRecording")}
          </button>
        )}
      </div>

      {preview && (
        <div className="ss-capture-preview" style={{ marginTop: 16, border: "1px solid var(--border)", borderRadius: 12, padding: 12, background: "var(--surface-container)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <b>{tr("capturePreview")}</b>
            <span className="badge" style={{ fontSize: 11 }}>{preview.saved ? tr("captureSaved") : tr("captureNotSaved")}</span>
          </div>
          {preview.kind === "screenshot" ? (
            <img src={preview.url} alt="capture" style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 8, background: "#000" }} />
          ) : (
            <video src={preview.url} controls style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 8, background: "#000" }} />
          )}
          <div className="ss-capture-meta" style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", fontSize: 12, color: "var(--text-muted)" }}>
            <span>{tr("captureSource")}: {preview.source}</span>
            <span>{tr("captureTimestamp")}: {new Date(preview.timestamp).toLocaleString()}</span>
            <span>{tr("captureSize")}: {formatBytes(preview.size)}</span>
            {preview.durationSeconds !== undefined && <span>{tr("captureDuration")}: {preview.durationSeconds}s</span>}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <button className="ss-btn-ghost" type="button" onClick={() => void saveCurrentPreview(false)} disabled={preview.saved}>
              {tr("captureSave")}
            </button>
            <button className="ss-btn-ghost" type="button" onClick={() => void saveCurrentPreview(true)}>
              {tr("captureSaveAs")}
            </button>
            <button className="ss-btn-ghost" type="button" onClick={discardPreview}>
              {tr("captureDiscard")}
            </button>
          </div>
          <p className="muted" style={{ fontSize: 11, margin: "10px 0 0" }}>{tr("captureSessionOnly")}</p>
        </div>
      )}

      {message && <div className="msg info" style={{ marginTop: 12 }}>{message}</div>}
    </div>
  );
}
